import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Expense } from '../types/expense';
import {
  getAllExpenses,
  createExpense,
  updateExpense,
  type ExpenseCreateDto,
  type ExpensePatchDto,
} from '../lib/api/endpoints';
import { expenseFromDto } from '../lib/api/mappers';

export interface ExpenseFilter {
  categoryId?: string;
  vehicleId?: string;
  driverId?: string;
  tripId?: string;
  subunitId?: string;
  /** ⚠️ Filtran por `incurredAt` — cuándo se INCURRIÓ, no cuándo se digitó. */
  from?: string;
  to?: string;
}

/**
 * FE-12 — el listado de gastos y las dos operaciones que lo mueven.
 *
 * La visibilidad la aplica el servidor (§4.8): el Operador ve solo los suyos,
 * el Supervisor los de su sub-unidad —por la del vehículo **o** la del
 * conductor, porque un viático no tiene vehículo—, Administrador y Ejecutivo
 * el consolidado. Este hook NO vuelve a filtrar por rol ni por tenant: hacerlo
 * en el cliente sería fingir una autorización que no tenemos (contrato §7).
 *
 * `registrar` y `corregir` devuelven el gasto ya hidratado por el servidor
 * —con placa, nombre del conductor y recibo vigente— y lo reemplazan en la
 * lista sin recargarla entera. Lanzan `ApiError` tal cual: el `403` del
 * Ejecutivo y el `404` de un gasto ajeno son información de negocio.
 */
export function useExpenses(filter: ExpenseFilter = {}) {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  // El filtro llega como objeto literal desde el componente: sin esto, cada
  // render dispararía una carga nueva.
  const filterKey = JSON.stringify(filter);

  useEffect(() => {
    let cancelled = false;
    const parsed = JSON.parse(filterKey) as ExpenseFilter;

    const load = async () => {
      setIsLoading(true);
      try {
        const items = await getAllExpenses(parsed);
        if (cancelled) return;
        setExpenses(items.map(expenseFromDto));
        setError(null);
      } catch (err) {
        if (!cancelled) {
          setExpenses([]);
          setError(err instanceof Error ? err : new Error('Error desconocido cargando los gastos'));
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [filterKey, reloadToken]);

  const reload = useCallback(() => setReloadToken(t => t + 1), []);

  // El listado llega "el más reciente primero" por `incurredAt`, y así se
  // conserva: reemplazar en sitio no cambia el orden que decidió el servidor.
  // Un gasto NUEVO sí se reordena, porque su `incurredAt` puede ser de la
  // semana pasada — encabezar la lista con él lo pondría donde no va.
  const upsert = useCallback((expense: Expense) => {
    setExpenses(prev => {
      const idx = prev.findIndex(e => e.id === expense.id);
      if (idx !== -1) {
        const next = [...prev];
        next[idx] = expense;
        return next;
      }
      return [...prev, expense].sort((a, b) => Date.parse(b.incurredAt) - Date.parse(a.incurredAt));
    });
  }, []);

  const acciones = useMemo(() => ({
    registrar: async (data: ExpenseCreateDto): Promise<Expense> => {
      const expense = expenseFromDto(await createExpense(data));
      upsert(expense);
      return expense;
    },
    // Corregir el `incurredAt` puede sacar el gasto del expediente de un viaje
    // y meterlo en el de otro (ADR-024): quien muestre un expediente tiene que
    // volver a pedirlo.
    corregir: async (expenseId: string, data: ExpensePatchDto): Promise<Expense> => {
      const expense = expenseFromDto(await updateExpense(expenseId, data));
      upsert(expense);
      return expense;
    },
    /**
     * Reemplaza el gasto en la lista con lo que devolvió otra llamada. Lo usa
     * la subida de un recibo: el `Expense` trae el recibo VIGENTE embebido, y
     * sin esto la fila seguiría marcada «sin recibo» hasta recargar la página.
     */
    reemplazar: upsert,
  }), [upsert]);

  return { expenses, isLoading, error, reload, ...acciones };
}
